
import { Component, EventEmitter, Input, OnInit, OnChanges, Output, OnDestroy, ViewChild, ElementRef, Renderer2, HostListener, HostBinding, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, switchMap, tap, of, Observable, Subscription } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import ApiOptionsConfig from '../../../services/api/api-options.config';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { LoaderConfig } from 'src/app/components/widgets/loader/loader.config';

@Component({
  selector: 'autocomplete-field',
  imports: [ReactiveFormsModule, LoaderComponent],
  templateUrl: './autocomplete-field.component.html',
  styleUrl: './autocomplete-field.component.scss'
})
export class AutocompleteFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() options: Array<{ key: string | number; value: string }> = [];
  @Input() inputFormControl!: FormControl;
  @Input() readonly!: boolean;
  @Input() displayKeyValue: boolean = false;
  @Input() valueType: 'string' | 'number' = 'string';
  @Input() apiService: any; 
  @Input() apiMethod: string = ''; 
  @Input() optionsKey: string = ''; 
  @Input() valueOption:string = 'value';
  @Input() keyOption:string = 'key';
  @Input() useApiSearch: boolean = false; 
  @Input() nameApiSearch?: string;
  @Input() debounceTime: number = 300;
  @Input() minChars: number = 2; 
  @Input() preventAutoOpen: boolean = false; 
  @Input() useLazyLoading: boolean = false;
  @Input() pageSize: number = 20;
  
  @Output() valueChange = new EventEmitter<any>();
  @Output() optionsLoaded = new EventEmitter<Array<{ key: string | number; value: string }>>();
  
  @ViewChild('inputElement') inputElement!: ElementRef;
  
  filteredOptions: Array<{ key: string | number; value: string }> = [];
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  hasMoreData: boolean = true;
  isDropdownOpen: boolean = false;
  searchInput$ = new Subject<string>();
  subscription: Subscription | null = null;
  clickOutsideSubscription: any;

  private isInitialLoad = true;
  private initialLoadComplete = false;
  private dropdownOpenTimeout: any;
  private currentPage: number = 0;
  private currentSearchTerm: string = '';
  
  dropdownLoaderConfig: LoaderConfig = {
    type: 'spinner',
    size: 'sm',
    overlay: true,
    text: 'Loading...',
  };
  
  constructor(private renderer: Renderer2) {}
  
  @HostBinding('class.dropdown-open')
  get isDropdownOpenClass(): boolean { return this.isDropdownOpen; }
  
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;
    if (this.inputElement && !this.inputElement.nativeElement.contains(targetElement)) {
      this.isDropdownOpen = false;
    }
  }

  ngOnInit() {
    this.isDropdownOpen = false;
    this.isInitialLoad = true;
    this.initialLoadComplete = false;
    
    if (this.useApiSearch) {
      this.setupApiSearch();
    } else {
      this.loadOptionsSelect();
    }
    
    setTimeout(() => {
      this.initialLoadComplete = true;      
      setTimeout(() => {
        this.isInitialLoad = false;        
      }, 500);
    }, 1000);
    setTimeout(() => {
      this.initializeDisplayValueFromKey();
    }, 600);
  }


  initializeDisplayValueFromKey() {
    const initialValue = this.inputFormControl.value;
    
    
    if (this.valueType === 'number' && initialValue !== null && initialValue !== undefined && initialValue !== '') {
      const numericValue = this.convertToNumber(initialValue);
      
      if (this.useApiSearch && this.apiService && this.apiMethod) {
        this.isLoading = true;
        const params = new HttpParams().set('size', '9999');
        const options: ApiOptionsConfig = { params };
        
        this.apiService[this.apiMethod](options).subscribe(
          (response: any) => {
            this.isLoading = false;
            if (response && response.data && response.data[this.optionsKey]) {
              
              const allOptions = response.data[this.optionsKey].map((option: any) => ({
                key: this.convertToNumber(option[this.keyOption]),
                value: option[this.valueOption]
              }));
              
              const matchingOption = allOptions.find((opt:any) => 
                Number(opt.key) === Number(numericValue)
              );
              
              if (matchingOption) {
                this.inputFormControl.setValue(matchingOption.value, { emitEvent: false });
                
                this.valueChange.emit(matchingOption.key);
              }
            }
          },
          (error: any) => {
            this.isLoading = false;
          }
        );
      } else {
        const option = this.findOptionByKey(numericValue);
        if (option) {
          this.inputFormControl.setValue(option.value, { emitEvent: false });
          this.valueChange.emit(option.key);
        }
      }
    } else if (initialValue === null && this.valueType === 'number') {
      this.valueChange.emit(null);
      
      if (this.inputFormControl.value !== '') {
        this.inputFormControl.setValue('', { emitEvent: false });
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && !this.useApiSearch && this.options.length > 0) {
      const currentValue = (this.inputFormControl?.value || '').toLowerCase();
      if (this.displayKeyValue || this.valueType === 'number') {
        this.filteredOptions = this.options.filter(option =>
          typeof option === 'object' && 'value' in option &&
          option.value.toLowerCase().includes(currentValue)
        );
      } else {
        this.filteredOptions = this.options;
      }
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.searchInput$.complete();
  }

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    
    if (!this.readonly) {
      this.isDropdownOpen = !this.isDropdownOpen;
      
      if (this.isDropdownOpen) {
        if (!this.useApiSearch) {
          this.filteredOptions = this.options;
        } else if (this.inputFormControl.value && this.inputFormControl.value.length >= this.minChars) {
          if (this.useLazyLoading) {
            this.currentPage = 0;
            this.hasMoreData = true;
            this.filteredOptions = [];
            this.currentSearchTerm = '';
          }
          this.searchInput$.next(this.inputFormControl.value);
        } else if (this.inputFormControl.value === '') {
          if (this.useLazyLoading) {
            this.currentPage = 0;
            this.currentSearchTerm = '';
            this.hasMoreData = true;
            this.filteredOptions = [];
          }
          let params = this.useLazyLoading
            ? new HttpParams().set('page', 0).set('size', this.pageSize)
            : new HttpParams().set('size', '9999');
          const options: ApiOptionsConfig = { params };
          
          if (this.apiService && this.apiMethod && typeof this.apiService[this.apiMethod] === 'function') {
            this.isLoading = true;
            this.apiService[this.apiMethod](options).subscribe(
              (response: any) => {
                this.isLoading = false;
                if (response && response.data && response.data[this.optionsKey]) {
                  const newOptions = response.data[this.optionsKey].map((option: any) => ({
                    key: option[this.keyOption],
                    value: option[this.valueOption]
                  }));
                  if (this.useLazyLoading) {
                    this.hasMoreData = newOptions.length >= this.pageSize;
                  }
                  this.filteredOptions = newOptions;
                } else {
                  this.filteredOptions = [];
                  if (this.useLazyLoading) this.hasMoreData = false;
                }
                this.optionsLoaded.emit(this.filteredOptions);
              },
              (error:any) => {
                console.error('Errore nella chiamata API:', error);
                this.isLoading = false;
                this.filteredOptions = [];
              }
            );
          }
        }
      }
    }
  }

  onDropdownScroll(event: Event): void {
    if (!this.useLazyLoading || this.isLoadingMore || !this.hasMoreData || this.isLoading) return;

    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 50) {
      this.loadNextPage();
    }
  }

  loadNextPage(): void {
    if (!this.useLazyLoading || this.isLoadingMore || !this.hasMoreData) return;

    this.currentPage++;
    this.isLoadingMore = true;

    const name = this.nameApiSearch ? this.nameApiSearch : this.label;
    const paramName = this.toCamelCase(name || 'search');
    let params = new HttpParams()
      .set('page', this.currentPage)
      .set('size', this.pageSize);

    if (this.currentSearchTerm && this.currentSearchTerm.length >= this.minChars) {
      params = params.set(paramName, this.currentSearchTerm);
    }

    const options: ApiOptionsConfig = { params };

    if (this.apiService && this.apiMethod && typeof this.apiService[this.apiMethod] === 'function') {
      this.apiService[this.apiMethod](options).subscribe({
        next: (response: any) => {
          this.isLoadingMore = false;
          if (response && response.data && response.data[this.optionsKey]) {
            const newOptions = response.data[this.optionsKey].map((option: any) => ({
              key: option[this.keyOption],
              value: option[this.valueOption]
            }));
            this.hasMoreData = newOptions.length >= this.pageSize;
            this.filteredOptions = [...this.filteredOptions, ...newOptions];
            this.optionsLoaded.emit(this.filteredOptions);
          } else {
            this.hasMoreData = false;
          }
        },
        error: () => {
          this.isLoadingMore = false;
          this.currentPage--;
        }
      });
    } else {
      this.isLoadingMore = false;
      this.currentPage--;
    }
  }

  onFocus() {
    if (!this.readonly) {
      if (this.inputFormControl.value && this.inputFormControl.value.length >= this.minChars && this.useApiSearch) {
        this.searchInput$.next(this.inputFormControl.value);
      }
    }
  }

  setupApiSearch() {
    this.subscription = this.searchInput$.pipe(
      tap(term => {
        if (this.isInitialLoad && !this.initialLoadComplete) {
          this.isDropdownOpen = false;
        }
        if (this.useLazyLoading && term !== this.currentSearchTerm) {
          this.currentPage = 0;
          this.hasMoreData = true;
          this.filteredOptions = [];
        }
        this.currentSearchTerm = term;
      }),
      debounceTime(this.debounceTime),
      distinctUntilChanged(),
      tap(() => this.isLoading = true),
      switchMap((term: string) => {
        if (term.length < this.minChars) {
          this.isLoading = false;
          return of([]);
        }
        const name = this.nameApiSearch ? this.nameApiSearch : this.label;
        const paramName = this.toCamelCase(name || 'search');
        let params = new HttpParams()
          .set(paramName, term)
          .set('size', this.useLazyLoading ? this.pageSize : 9999);
        if (this.useLazyLoading) {
          params = params.set('page', 0);
        }
        const options: ApiOptionsConfig = { params };
        
        if (this.apiService && this.apiMethod && typeof this.apiService[this.apiMethod] === 'function') {
          return this.apiService[this.apiMethod](options).pipe(
            tap(
              () => {
                this.isLoading = false;
              },
              (error) => {
                console.error('Errore nella chiamata API:', error);
                this.isLoading = false;
                return [];
              }
            )
          );
        } else {
          console.error('Servizio API o metodo non valido:', this.apiService, this.apiMethod);
          this.isLoading = false;
          return of([]);
        }
      })
    ).subscribe((response: any) => {
      this.isLoading = false;
      if (response && response.data && response.data[this.optionsKey]) {        
        const newOptions = response.data[this.optionsKey].map((option: any) => {
          let key = option[this.keyOption];
          let value = option[this.valueOption];
          
          if (key === undefined) {
            key = option.customerId || option.id || option.key;
          }
          
          if (value === undefined) {
            value = option.companyName || option.name || option.value || option.title || String(key);
          }
          
          return {
            key: key,
            value: String(value) 
          };
        });

        if (this.useLazyLoading) {
          this.hasMoreData = newOptions.length >= this.pageSize;
        }
        this.filteredOptions = newOptions;
        
        if (!this.isInitialLoad && this.initialLoadComplete && !this.preventAutoOpen) {
          this.isDropdownOpen = true;
        }
      } else {
        this.filteredOptions = [];
        if (this.useLazyLoading) {
          this.hasMoreData = false;
        }
      }
      this.optionsLoaded.emit(this.filteredOptions);
    });

    this.inputFormControl.valueChanges.subscribe(value => {
      if (this.isInitialLoad && !this.initialLoadComplete) {
        return;
      }
      
      if (value && value.length >= this.minChars) {
        this.searchInput$.next(value);
        if (!this.isInitialLoad && this.initialLoadComplete && !this.preventAutoOpen) {
          this.isDropdownOpen = true;
        }
      } else if (value === '') {
        if (this.useApiSearch) {
          if (this.useLazyLoading) {
            this.currentPage = 0;
            this.currentSearchTerm = '';
            this.hasMoreData = true;
            this.filteredOptions = [];
          }
          let params = this.useLazyLoading
            ? new HttpParams().set('page', 0).set('size', this.pageSize)
            : new HttpParams().set('size', '9999');
          const options: ApiOptionsConfig = { params };
          
          if (this.apiService && this.apiMethod && typeof this.apiService[this.apiMethod] === 'function') {
            this.isLoading = true;
            this.apiService[this.apiMethod](options).subscribe(
              (response: any) => {
                this.isLoading = false;
                if (response && response.data && response.data[this.optionsKey]) {
                  const newOptions = response.data[this.optionsKey].map((option: any) => ({
                    key: option[this.keyOption],
                    value: option[this.valueOption]
                  }));
                  if (this.useLazyLoading) {
                    this.hasMoreData = newOptions.length >= this.pageSize;
                  }
                  this.filteredOptions = newOptions;
                  
                  if (!this.isInitialLoad && this.initialLoadComplete && !this.preventAutoOpen) {
                    this.isDropdownOpen = true;
                  }
                } else {
                  this.filteredOptions = [];
                  if (this.useLazyLoading) this.hasMoreData = false;
                }
                this.optionsLoaded.emit(this.filteredOptions);
              },
              (error:any) => {
                console.error('Errore nella chiamata API:', error);
                this.isLoading = false;
                this.filteredOptions = [];
              }
            );
          } else {
            this.filteredOptions = [];
          }
        } else {
          this.filteredOptions = this.options;
          if (!this.isInitialLoad && this.initialLoadComplete && !this.preventAutoOpen) {
            this.isDropdownOpen = true;
          }
        }
      } else {
        this.filteredOptions = [];
        this.isDropdownOpen = false;
      }
    });
  }

  loadOptionsSelect() {    
    
    if (this.valueType === 'number' && this.inputFormControl.value) {
      const numValue = Number(this.inputFormControl.value);
      const option = this.findOptionByKey(numValue);
      if (option) {
        this.inputFormControl.setValue(option.value, { emitEvent: false });
      }
    }
    
    this.inputFormControl?.valueChanges?.subscribe(value => {
      if (!this.useApiSearch) {
        if (this.displayKeyValue || this.valueType === 'number') {
          this.filteredOptions = this.options?.filter(option =>
            typeof option === 'object' && 'value' in option && 
            option?.value?.toLowerCase()?.includes((value || '')?.toLowerCase())
          );
          
          if (this.valueType === 'number') {
            const matchingOption = this.options?.find(opt => 
              opt.value.toLowerCase() === (value || '').toLowerCase()
            );
            if (matchingOption) {
              this.valueChange.emit(matchingOption.key);
            }
          }
        } else {
          this.filteredOptions = this.options;
        }
        
        if ((value !== this.inputFormControl.value || value === '') && 
             this.filteredOptions.length > 0 && !this.isInitialLoad && !this.preventAutoOpen) {
          this.isDropdownOpen = true;
        }
      }
    });
  }

  onInput(event: any) {
    const value = event.target.value;
    
    this.isInitialLoad = false;
    this.preventAutoOpen = false;
    
    if (value === '' && this.valueType === 'number') {
      this.valueChange.emit(null);
    }
    
    if (this.useApiSearch) {
      if (value && value.trim().length > 0) {
        this.searchInput$.next(value);
        if (!this.preventAutoOpen) {
          this.isDropdownOpen = true;
        }
      } else if (value === '') {
        if (!this.preventAutoOpen) {
          this.isDropdownOpen = true;
        }
        if (this.useApiSearch) {
          if (!this.useLazyLoading) {
            const params = new HttpParams().set('size', '9999');
            const options: ApiOptionsConfig = { params };
            
            if (this.apiService && this.apiMethod && typeof this.apiService[this.apiMethod] === 'function') {
              this.isLoading = true;
              this.apiService[this.apiMethod](options).subscribe(
                (response: any) => {
                  this.isLoading = false;
                  if (response && response.data && response.data[this.optionsKey]) {
                    this.filteredOptions = response.data[this.optionsKey].map((option: any) => ({
                      key: option[this.keyOption],
                      value: option[this.valueOption]
                    }));
                  } else {
                    this.filteredOptions = [];
                  }
                  this.optionsLoaded.emit(this.filteredOptions);
                },
                (error:any) => {
                  console.error('Errore nella chiamata API:', error);
                  this.isLoading = false;
                  this.filteredOptions = [];
                }
              );
            }
          }
        } else {
          this.filteredOptions = this.options;
        }
      }
    } else {
      this.filteredOptions = value ?
        this.options.filter(option =>
          typeof option === 'object' && 'value' in option &&
          option.value.toLowerCase().includes(value.toLowerCase())
        ) : this.options;

      if (this.valueType === 'number') {
        const exactMatch = this.filteredOptions.find(option =>
          option.value.toLowerCase() === value.toLowerCase()
        );
        if (exactMatch) {
          this.valueChange.emit(exactMatch.key);
        }
      }

      if (!this.preventAutoOpen) {
        this.isDropdownOpen = true;
      }
    }
  }

  onOptionSelect(option: { key: string | number; value: string } | string): void {
    if (this.displayKeyValue && typeof option === 'object' && 'key' in option) {
      this.inputFormControl?.setValue(option.value);
      this.valueChange.emit(option.key);
    } else {
      if(typeof option === 'object' && 'value' in option){
        this.inputFormControl?.setValue(option.value);
        this.valueChange.emit(option.value);
      }
    }
    this.isDropdownOpen = false;
  }

  onOptionSelectNumber(option: { key: string | number; value: string } | string): void {
    if (typeof option === 'object' && 'key' in option) {
      this.inputFormControl?.setValue(option.value);
      
      if (option.key === null) {
        this.valueChange.emit(null);
      } else {
        const numericKey = this.convertToNumber(option.key);
        this.valueChange.emit(numericKey);
      }
    } else if (option === null || option === '') {
      this.valueChange.emit(null);
      this.inputFormControl?.setValue('', { emitEvent: false });
    }
    this.isDropdownOpen = false;
  }

  findOptionByKey(key: number | string): { key: string | number; value: string } | undefined {
    
    if (key === null) {
      const nullOption = this.options.find(opt => opt.key === null);
      if (nullOption) {
        return nullOption;
      }
      return undefined;
    }
    
    if (key === undefined) return undefined;
    const numKey = this.convertToNumber(key);
    
    const exactMatch = this.options.find(opt => opt.key === key);
    if (exactMatch) {
      return exactMatch;
    }
    
    const numberMatch = this.options.find(opt => 
      !isNaN(numKey) && Number(opt.key) === numKey
    );
    if (numberMatch) {
      return numberMatch;
    }
    
    const stringMatch = this.options.find(opt => 
      String(opt.key) === String(key)
    );
    
    return stringMatch;
  }

  private convertToNumber(value: any): number {
    
    if (value === null) {
      return null as unknown as number; 
    }
    
    if (value === undefined || value === '') {
      return NaN;
    }
    
    const cleanValue = typeof value === 'string' ? value.trim() : value;
    
    const num = Number(cleanValue);
    
  
    if (isNaN(num)) {
    }
    
    return num;
  }

  private toCamelCase(str: string): string {
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      })
      .replace(/\s+/g, '');
  }

  setPreventAutoOpen(value: boolean): void {
    this.preventAutoOpen = value;
  }
}
